import {
  convertArbitraryPhoneNumberToE164,
  isValidE164PhoneNumber,
} from "@redotech/phone/number";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import {
  PhoneNumberProvider,
  PhoneNumberUseCase,
  TextMessagingVerificationStatus,
} from "@redotech/redo-common-model/phone/phone-number";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

export const supportPhoneNumberForm = groupInput({
  phoneNumber: input<string>(),
});

export const supportPhoneNumberDefault: SupportPhoneNumberValue = {
  phoneNumber: "",
};

type SupportPhoneNumberValue = InputSchema.Value<typeof supportPhoneNumberForm>;

const PHONE_NUMBER_QUERY_KEY = "supportPhoneNumber";

export const PhonesCard = memo(function PhonesCard({
  teamId,
}: {
  teamId: string;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const queryClient = useQueryClient();

  const phoneNumberInput = useInput(
    supportPhoneNumberForm,
    supportPhoneNumberDefault,
  );

  const existingPhoneNumberQuery = useQuery({
    queryKey: [PHONE_NUMBER_QUERY_KEY, teamId],
    queryFn: () =>
      rpcClient.getPhoneNumberByTeamIdAndUseCase({
        teamId,
        useCase: PhoneNumberUseCase.Support,
      }),
  });

  const savePhoneNumberMutation = useMutation({
    mutationFn: async (phoneNumber: string) => {
      const e164Number = convertArbitraryPhoneNumberToE164(phoneNumber);
      if (!e164Number || !isValidE164PhoneNumber(e164Number)) {
        throw new Error("Please enter a valid phone number");
      }

      return rpcClient.createPhoneNumber({
        team: teamId,
        number: e164Number,
        provider: PhoneNumberProvider.Telnyx,
        useCase: PhoneNumberUseCase.Support,
        verificationStatus: TextMessagingVerificationStatus.Verified,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PHONE_NUMBER_QUERY_KEY, teamId],
      });
      phoneNumberInput.inputs.phoneNumber.setValue("");
      toast("Support phone number saved successfully", { variant: "success" });
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast(`Failed to save phone number: ${errorMessage}`, {
        variant: "error",
      });
    },
  });

  const phoneInputs = phoneNumberInput.inputs;

  const isValidPhoneNumber = (() => {
    const e164Number = convertArbitraryPhoneNumberToE164(
      phoneInputs?.phoneNumber?.value || "",
    );
    return e164Number && isValidE164PhoneNumber(e164Number);
  })();

  return (
    <Card radius="sm" title="Phone Number">
      {existingPhoneNumberQuery.data ? (
        <Flex dir="column" gap="md">
          <Text fontSize="sm">
            Current support phone number:{" "}
            <strong>{existingPhoneNumberQuery.data.number}</strong>
          </Text>
          <Text fontSize="xs" textColor="tertiary">
            This phone number is used for support conversations and voice calls.
          </Text>
        </Flex>
      ) : (
        <Flex dir="column" gap="md">
          <RedoTextInput
            description="Enter a phone number for support conversations. Enter the number in E.164 format, e.g., +15551234567."
            label="Phone Number"
            placeholder="+15551234567"
            setValue={phoneInputs.phoneNumber.setValue}
            size="sm"
            value={phoneInputs.phoneNumber.value}
          />
          <Flex>
            <RedoButton
              disabled={
                !phoneInputs.phoneNumber.value ||
                savePhoneNumberMutation.isPending ||
                !isValidPhoneNumber
              }
              hierarchy="primary"
              onClick={() =>
                savePhoneNumberMutation.mutate(phoneInputs.phoneNumber.value)
              }
              size="sm"
              text={
                savePhoneNumberMutation.isPending
                  ? "Saving..."
                  : "Save phone number on merchant"
              }
            />
          </Flex>
        </Flex>
      )}
    </Card>
  );
});
